import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Section, Card, Badge, Table, Alert, Button } from "../design-system";
import { useAuth } from "../auth/AuthProvider";

export function EmailImportPendingCard() {
  const { apiGet } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await apiGet("/api/admin/order-imports/email-pending");
        if (alive) setData(r);
      } catch (e) {
        // v43.35.9 — never display the literal "err" placeholder.
        // Extract status from every apiGet error shape (fetch-based helper
        // exposes `.status`; axios-based helper exposes `.response.status`).
        // Fallback to a user-friendly Italian message.
        if (alive) {
          const status = e?.status ?? e?.response?.status ?? null;
          setErr(status || "generic");
        }
      }
    })();
    return () => { alive = false; };
  }, []);
  return (
    <Section
      title="Ordini da verificare"
      subtitle="Bozze da ordini via email e caricamenti Customer — non ancora contate come ordini reali"
      actions={data && (
        <Badge tone={data.count_total > 0 ? "warning" : "neutral"}
               data-testid="email-import-pending-badge">
          {data.count_total || 0}
        </Badge>
      )}
    >
      <Alert tone="info" title="Non sono ordini commerciali">
        Queste righe sono <strong>OrderImport in attesa di revisione</strong>.
        Non entrano in Picking, Piano Carichi, DDT, Statistiche o Vettori
        finché un operatore non le conferma dalla pagina Import ordini.
      </Alert>
      <Card style={{ borderLeft: "4px solid #f59e0b" }} data-testid="email-import-pending-card">
        <Card.Body>
          {err === 403 && <Alert tone="warning" data-testid="email-import-pending-forbidden">Permesso negato.</Alert>}
          {err && err !== 403 && (
            <Alert tone="danger" data-testid="email-import-pending-error">
              Impossibile caricare gli ordini email da verificare.
              {typeof err === "number" ? ` (HTTP ${err})` : ""}
            </Alert>
          )}
          {!data && !err && <span className="muted">Caricamento…</span>}
          {data && data.items && data.items.length === 0 && (
            <span className="muted" data-testid="email-import-pending-empty">
              Nessun ordine in attesa di verifica.
            </span>
          )}
          {data && data.items && data.items.length > 0 && (
            <>
              <div style={{ display: "flex", gap: 12, marginBottom: 10, fontSize: 13 }}>
                <span data-testid="email-import-count-ready">
                  <strong>READY:</strong> {data.count_ready}
                  <span className="muted"> (verifica finale)</span>
                </span>
                <span data-testid="email-import-count-review">
                  <strong>REVIEW:</strong> {data.count_review_required}
                  <span className="muted"> (anomalie)</span>
                </span>
              </div>
              <Table
                columns={[
                  { key: "customer_code", label: "Codice" },
                  { key: "customer_name", label: "Cliente" },
                  { key: "source_type", label: "Origine",
                    render: (r) => (
                      <span data-testid={`email-import-source-${r.id}`}
                            style={{ fontSize: 11, padding: "2px 6px",
                                     borderRadius: 4, fontWeight: 600,
                                     background: r.source_type === "CUSTOMER_UPLOAD"
                                       ? "#e0f2fe" : "#fce7f3",
                                     color: r.source_type === "CUSTOMER_UPLOAD"
                                       ? "#075985" : "#9d174d" }}>
                        {r.source_type === "CUSTOMER_UPLOAD"
                          ? "Caricato dal Customer"
                          : "Email"}
                      </span>
                    ) },
                  { key: "sender_email", label: "Mittente",
                    render: (r) => r.sender_email
                      ? <code style={{ fontSize: 12 }}>{r.sender_email}</code>
                      : <span className="muted">—</span> },
                  { key: "created_at", label: "Data",
                    render: (r) => r.created_at
                      ? new Date(r.created_at).toLocaleString("it-IT")
                      : "—" },
                  { key: "status", label: "Stato",
                    render: (r) => (
                      <Badge tone={r.status === "READY" ? "success" : "warning"}
                             data-testid={`email-import-status-${r.id}`}>
                        {r.status === "READY" ? "Pronto" : "Verifica"}
                      </Badge>
                    ) },
                  { key: "line_count", label: "Righe",
                    render: (r) => (
                      <span>
                        {r.line_count}
                        {r.anomaly_count > 0 && (
                          <span style={{ color: "#b45309", marginLeft: 6 }}
                                data-testid={`email-import-anomalies-${r.id}`}>
                            ⚠ {r.anomaly_count}
                          </span>
                        )}
                      </span>
                    ) },
                  { key: "actions", label: "",
                    render: (r) => (
                      <Link to={`/admin/order-imports/${r.id}`}
                            data-testid={`email-import-review-cta-${r.id}`}
                            style={{
                              display: "inline-block", padding: "4px 10px",
                              background: "#0F4C81", color: "#fff",
                              textDecoration: "none", borderRadius: 6,
                              fontSize: 12, fontWeight: 600,
                            }}>
                        Verifica →
                      </Link>
                    ) },
                ]}
                rows={data.items}
                emptyText="Nessun risultato"
              />
            </>
          )}
        </Card.Body>
      </Card>
    </Section>
  );
}

function StaffDashboard({ roleLabel, panels, dataTestId }) {
  const { user, apiGet } = useAuth();
  const [state, setState] = useState({});
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      const next = {};
      for (const p of panels) {
        try { next[p.key] = await apiGet(p.endpoint); }
        catch (e) { next[p.key] = { __error: e?.response?.status || "err" }; }
      }
      setState(next);
    })();
  }, []);

  return (
    <div data-testid={dataTestId}>
      <Section title={`Dashboard ${roleLabel}`} subtitle={`Utente: ${user?.email}`}
               actions={<Badge tone="info">Fase 2A</Badge>}>
        <Alert tone="info" title="Foundation attiva">
          Questa dashboard mostra i dati leggibili dai <strong>permessi effettivi</strong> del tuo ruolo.
          I permessi sono modificabili dall'Admin senza modifica del codice.
        </Alert>
      </Section>
      {panels.map((p) => {
        const data = state[p.key];
        return (
          <Section key={p.key} title={p.title}>
            {!data && <Card><Card.Body className="muted">Caricamento…</Card.Body></Card>}
            {data?.__error === 403 && <Alert tone="warning">Permesso negato per <code>{p.endpoint}</code>.</Alert>}
            {data?.__error && data.__error !== 403 && <Alert tone="danger">Errore {data.__error}</Alert>}
            {Array.isArray(data) && (
              <Table columns={p.columns} rows={data} emptyText="Nessun risultato" />
            )}
          </Section>
        );
      })}
    </div>
  );
}

export function SalesDashboard() {
  return <StaffDashboard
    dataTestId="sales-dashboard"
    roleLabel="Operatore Commerciale"
    panels={[
      { key: "customers", endpoint: "/api/customers", title: "Clienti",
        columns: [
          { key: "code", label: "Codice" },
          { key: "business_name", label: "Ragione sociale" },
          { key: "region", label: "Regione" },
          { key: "status", label: "Stato" },
        ]},
      { key: "price_lists", endpoint: "/api/price-lists", title: "Listini",
        columns: [
          { key: "code", label: "Codice" },
          { key: "name", label: "Nome" },
          { key: "valid_from", label: "Valido da" },
          { key: "is_active", label: "Attivo", render: (r) => r.is_active ? "Sì" : "No" },
        ]},
    ]} />;
}

export function LogisticsDashboard() {
  return (
    <>
      <EmailImportPendingCard />
      <StaffDashboard
        dataTestId="logistics-dashboard"
        roleLabel="Operatore Logistico"
        panels={[
          { key: "customers", endpoint: "/api/customers", title: "Clienti",
            columns: [
              { key: "code", label: "Codice" },
              { key: "business_name", label: "Ragione sociale" },
              { key: "region", label: "Regione" },
            ]},
          { key: "delivery_points", endpoint: "/api/delivery-points", title: "Punti di consegna",
            columns: [
              { key: "name", label: "Nome" },
              { key: "city", label: "Città" },
              { key: "region", label: "Regione" },
              { key: "truck_accessible", label: "Bilico", render: (r) => r.truck_accessible === true ? "SI" : r.truck_accessible === false ? "NO" : "DA VERIFICARE" },
              { key: "tail_lift_required", label: "Sponda", render: (r) => r.tail_lift_required === true ? "SI" : r.tail_lift_required === false ? "NO" : "DA VERIFICARE" },
            ]},
        ]} />
    </>
  );
}

export function AdminDashboard() {
  const { apiGet } = useAuth();
  const [matrix, setMatrix] = useState(null);
  useEffect(() => {
    (async () => {
      try { setMatrix(await apiGet("/api/rbac/matrix")); } catch {}
    })();
  }, []);
  return (
    <div data-testid="admin-dashboard">
      <Section title="Dashboard Admin" subtitle="Accesso completo a tutti i moduli"
               actions={<Badge tone="success">ADMIN</Badge>}>
        <Alert tone="info">
          Fase 2A operativa: gestione utenti/ruoli/permessi (matrice), anagrafiche, catalogo,
          listini, documenti tecnici e audit log. Le fasi successive aggiungeranno pricing engine,
          PFA, ordini, spedizioni.
        </Alert>
      </Section>
      <Section title="Matrice Ruoli & Permessi" subtitle="Modificabile senza deploy (permessi non admin_only)">
        {!matrix && <Card><Card.Body className="muted">Caricamento…</Card.Body></Card>}
        {matrix && (
          <div className="ds-table-wrap">
            <table className="ds-table">
              <thead>
                <tr>
                  <th>Permesso</th>
                  <th>Modulo</th>
                  {matrix.roles.map((r) => <th key={r.id}>{r.key}</th>)}
                </tr>
              </thead>
              <tbody>
                {matrix.permissions.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <div style={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
                        <code style={{ fontSize: ".8rem" }}>{p.key}</code>
                        {p.admin_only && <Badge tone="warning">admin_only</Badge>}
                      </div>
                    </td>
                    <td className="muted" style={{ fontSize: ".85rem" }}>{p.module}</td>
                    {matrix.roles.map((r) => {
                      const granted = matrix.matrix[`${r.id}:${p.id}`] === true;
                      const isAdminRow = r.key === "ADMIN";
                      return (
                        <td key={r.id} style={{ textAlign: "center" }}>
                          {isAdminRow ? "✓" : granted ? "✓" : "—"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
